import React, { useRef } from 'react';
import { useStyletron } from 'baseui';
import usePreviousValue from '../../hooks/usePreviousValue';
import { useReviewMode } from '../../providers/review-mode-providers';
import { usePubSub } from '../../pub-sub';
import { useReviewFeature } from '../hooks/use-review-feature';
import { QuestionMarkIcon } from '../icons/question-mark-icon';
import { PanelStateRenderer } from '../panel-state-renderer';
import { EVENTS } from '..';
import { BatchItem } from './batch-item';
import type { BatchItemT } from './types';
import { useGetBatchModePermissions } from './useGetBatchModePermissions';
import { getUpdatedItem } from './utils';
type BatchListProps = {
  batchItems: BatchItemT[];
  selectedId?: string;
};
export const BatchList = ({
  batchItems,
  selectedId
}: BatchListProps) => {
  const [css, theme] = useStyletron();
  const previousSelectedId = usePreviousValue(selectedId);
  const permissions = useGetBatchModePermissions();
  const {
    publish
  } = usePubSub();
  const batchListRef = useRef<HTMLDivElement>(null);
  const {
    enableContinuousPlayback
  } = useReviewFeature();
  const {
    selectAnnotation
  } = useReviewMode();
  const onSelectAnnotation = (id: string) => {
    selectAnnotation?.(id);
    publish(EVENTS.BATCH_ITEM_SELECTED);
  };
  const onPlayComplete = () => {
    batchListRef.current?.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  if (batchItems.length === 0) {
    return <PanelStateRenderer icon={<QuestionMarkIcon />} title="No Filtered Objects" description="No objects to review. You can filter the objects by label." />;
  }
  return <div className={css({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.sizing.scale600,
    overflow: 'auto',
    maxHeight: '75%',
    scrollBehavior: 'smooth',
    paddingBottom: theme.sizing.scale3200 // to account for the bottom padding of the batch item for scroll effect
  })} ref={batchListRef}>
      {batchItems.map((item, index) => {
      const updatedItem = getUpdatedItem({
        item,
        index,
        selectedId,
        batchItems,
        permissions,
        previousSelectedId
      });
      const isExpanded = enableContinuousPlayback ? selectedId === item.id : true;
      const handleOnPlayComplete = enableContinuousPlayback && index === batchItems.length - 1 ? onPlayComplete : undefined;
      return <div key={item.id} tabIndex={index} className={css({
        cursor: 'pointer'
      })} onKeyDown={event => {
        if (event.key === 'Enter') {
          onSelectAnnotation(item.id);
        }
      }} onClick={() => {
        onSelectAnnotation(item.id);
      }} role="button">
            <BatchItem item={updatedItem} expanded={isExpanded} onPlayComplete={handleOnPlayComplete} disableFocus={!enableContinuousPlayback} />
          </div>;
    })}
    </div>;
};